import React, { useState, useRef, useEffect } from "react";
import { Row, Col, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink, faTrashAlt, faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import PropTypes from "prop-types";
import { useFormContext, get } from "react-hook-form";

import UploadBar from "components/atoms/UploadBar/UploadBar";
import errorToast from "helpers/errorToast";
import { FormattedMessage, useIntl } from "react-intl";
import { deleteFile, uploadFile } from "api";

const ProviderAttachmentFile = function ({ bucket, onAddFile, onDeleteFile }) {
  const intl = useIntl();
  const methods = useFormContext();
  const {
    getValues,
    clearErrors,
    formState: { errors },
  } = methods;
  const { bucket_name, is_active, is_mandatory, id } = bucket;
  const hiddenFileInput = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [uploadErrorMsg, setUploadErrorMsg] = useState();
  const [percentCompleted, setPercentCompleted] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const files = getValues("bucket_attachments");
    if (!files || selectedFile) return;
    const file = files.find((f) => f.id === id);
    setSelectedFile(file);
    clearErrors("bucket_attachments");
  }, [getValues("bucket_attachments")]);

  useEffect(() => {
    if (!selectedFile) return;
    onAddFile(selectedFile, bucket_name); // Adds file to list of all files in Attachments.js
  }, [selectedFile]);

  const uploadFiles = (file) => {
    if (!file) return;
    if (file.size >= 15_000_000) {
      setUploadError(true);
      setUploadErrorMsg(
        <>
          <div>
            {intl.formatMessage({
              id: "new-request.attachments.attachmentFile.uploadFailed",
              defaultMessage: "Upload failed. Please try again",
            })}
          </div>
          <div>
            {intl.formatMessage({
              id: "new-request.attachments.attachmentFile.maxUploadSize",
              defaultMessage: "(max filesize 15mb)",
            })}
          </div>
        </>,
      );
      setUploadInProgress(false);
      return;
    }
    if (selectedFile) onDeleteFile(selectedFile); // If file is already uploaded, delete it from the list of all files before changing it to the new file
    setUploadInProgress(true);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("filename", file.name);

    uploadFile(formData, {
      onUploadProgress: (progressEvent) => {
        setPercentCompleted(Math.round((progressEvent.loaded * 100) / progressEvent.total));
      },
    })
      .then((res) => {
        if (res.status === 201) {
          const formattedFile = {
            filename: res.data.name.split("/").pop(),
            reference: res.data.reference_number,
            url: res.data.url,
            id,
          };
          setSelectedFile(formattedFile);
          setUploadInProgress(false);
          setPercentCompleted(0);
          setUploadError(false);
        }
      })
      .catch((error) => {
        const errMsgs = errorToast(error);
        setUploadError(true);
        setUploadErrorMsg(errMsgs?.[0]);
        setUploadInProgress(false);
      });
  };

  const handleDeleteFile = () => {
    const reference = selectedFile.file || selectedFile.reference;
    if (!reference) return;
    setIsLoading(true);
    deleteFile(reference)
      .then((res) => {
        if (res.status === 204) {
          onDeleteFile(selectedFile); // Deletes the file from the list of all files locally
          setSelectedFile(null);
        } else throw new Error("Could not delete file, please try again later");
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        if (err.response?.status === 404) {
          onDeleteFile(selectedFile); // Deletes the file from the list of all files locally
          setSelectedFile(null);
          return;
        }
        errorToast(err, "Could not delete file, please try again later");
      });
  };

  const handleClick = () => hiddenFileInput.current.click();

  const handleChange = (event) => {
    const file = event.target.files[0];
    uploadFiles(file);
  };

  if (!is_active) return null;

  return (
    <Row className="mb-1">
      <Col xs={4} className={`font-weight-bold mb-1 no-wrap ${is_mandatory ? "required" : ""}`}>
        {bucket_name}
      </Col>
      <Col xs={4}>
        {uploadInProgress && <UploadBar percentCompleted={percentCompleted} />}
        {selectedFile && !uploadInProgress && !isLoading && (
          <div className="d-flex align-items-center">
            <a target="_blank" href={selectedFile.url} rel="noopener noreferrer" className="text-wrap break-all">
              {selectedFile.filename}
            </a>
            <div
              style={{ width: "12px" }}
              className="ml-md cursor-pointer text-danger mr-md"
              onClick={() => handleDeleteFile()}
            >
              <FontAwesomeIcon icon={faTrashAlt} />
            </div>
          </div>
        )}
        {isLoading && (
          <div className="d-flex align-items-center justify-content-center">
            <FontAwesomeIcon icon={faCircleNotch} className="font-16 text-secondary " spin />
          </div>
        )}
        {uploadError && !uploadInProgress && (
          <div className="text-danger font-b3">
            {uploadErrorMsg ? (
              <div>{uploadErrorMsg}</div>
            ) : (
              <>
                <div>
                  <FormattedMessage
                    id="new-request.attachments.attachmentFile.uploadFailed"
                    defaultMessage="Upload failed. Please try again"
                  />
                </div>
                <div>
                  <FormattedMessage
                    id="new-request.attachments.attachmentFile.maxUploadSize"
                    defaultMessage="(max filesize 15mb)"
                  />
                </div>
              </>
            )}
          </div>
        )}
      </Col>
      <Col xs={4} className="d-flex justify-content-center">
        <Row>
          <Button type="button" variant="link" name="file" onClick={handleClick}>
            <div className="flex-row d-flex" style={{ display: "inline-block !important" }}>
              <FontAwesomeIcon icon={faLink} />
              <p className="mb-0 no-wrap">
                {!selectedFile ? (
                  <FormattedMessage id="new-request.attachments.attachmentFile.attachFile" defaultMessage="Attach file" />
                ) : (
                  <FormattedMessage id="new-request.attachments.attachmentFile.changeFile" defaultMessage="Change file" />
                )}
                <input
                  type="file"
                  accept="application/pdf, image/gif, image/tiff, text/csv, application/vnd.apple.pages, application/vnd.apple.keynote, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.presentationml.presentation, application/msword, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.wordprocessingml.document, image/jpeg, image/png"
                  ref={hiddenFileInput}
                  className="hidden-file-input"
                  onChange={handleChange}
                  onClick={(e) => {
                    e.target.value = null;
                  }}
                />
              </p>
            </div>
          </Button>
        </Row>
        <Row className="ml-3 w-192">
          {get(errors, "bucket_attachments") && !selectedFile && is_mandatory && (
            <div className="d-block font-b2 invalid-feedback ml-4">
              *
              <FormattedMessage
                id="new-request.attachments.attachment.attachmentIsRequired"
                defaultMessage="This attachment is required"
              />
            </div>
          )}
        </Row>
      </Col>
    </Row>
  );
};

ProviderAttachmentFile.propTypes = {
  bucket: PropTypes.instanceOf(Object).isRequired,
  onAddFile: PropTypes.func.isRequired,
  onDeleteFile: PropTypes.func.isRequired,
};

export default ProviderAttachmentFile;
